import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Check } from 'lucide-react';
import { useI18n } from '../i18n/LanguageContext';
import { Reveal } from '../components/Reveal';

const IMGS = {
  hero: 'https://images.unsplash.com/photo-1647427060118-4911c9821b82?auto=format&fit=crop&w=2000&q=80',
  process: 'https://images.unsplash.com/photo-1593106410288-caf65eca7c9d?auto=format&fit=crop&w=1800&q=80',
};

export default function Sustainability() {
  const { t } = useI18n();
  return (
    <div data-testid="sustainability-page">
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-end overflow-hidden">
        <img src={IMGS.hero} alt="Circular economy" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-forest-deep via-forest/65 to-forest/20" />
        <div className="container-x relative z-10 py-20 md:py-28 text-sand">
          <Reveal className="kicker !text-rust">{t.sustainability.kicker}</Reveal>
          <Reveal delay={100}>
            <h1 className="display-1 mt-6 text-sand max-w-[18ch] text-balance">{t.sustainability.title}</h1>
          </Reveal>
        </div>
      </section>

      {/* Intro */}
      <section className="container-x py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-4">
            <div className="kicker">— ARC Catalonia</div>
          </div>
          <Reveal delay={80} className="md:col-span-8">
            <p className="lead">{t.sustainability.intro}</p>
          </Reveal>
        </div>
      </section>

      {/* Projects */}
      <section className="container-x pb-24 md:pb-32 space-y-20 md:space-y-28" data-testid="projects">
        {t.sustainability.projects.map((p, i) => {
          const reverse = i % 2 === 1;
          return (
            <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
              <Reveal className={`md:col-span-5 ${reverse ? 'md:order-2 md:col-start-8' : ''}`}>
                <div className="bg-sand-warm p-10 md:p-12 border border-forest/10">
                  <div className="kicker text-rust">{p.code}</div>
                  <h2 className="display-3 mt-4 text-forest">{p.name}</h2>
                  <p className="body-text mt-6">{p.body}</p>
                </div>
              </Reveal>
              <Reveal delay={120} className={`md:col-span-6 ${reverse ? 'md:order-1' : 'md:col-start-7'}`}>
                <div className="border-t border-forest/15 divide-y divide-forest/15">
                  {p.benefits.map((b, k) => (
                    <div key={k} className="py-5 flex items-start gap-5">
                      <Check size={18} className="text-rust mt-1 flex-shrink-0" />
                      <p className="text-forest/85 leading-relaxed">{b}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          );
        })}
      </section>

      {/* Partnership */}
      <section className="bg-forest text-sand">
        <div className="container-x py-20 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <Reveal className="md:col-span-8">
            <div className="kicker !text-rust">— Partnership</div>
            <p className="mt-6 font-serif text-2xl md:text-3xl font-light text-sand leading-snug">{t.sustainability.partnership}</p>
          </Reveal>
          <Reveal delay={120} className="md:col-span-4 flex md:justify-end">
            <Link to="/contacto" className="btn-primary !bg-rust hover:!bg-sand hover:!text-forest" data-testid="sustain-final-cta">
              {t.cta.contact} <ArrowUpRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
